import type { LatLon } from '../../lib/types'

/**
 * Schematische Mercator-Projektion ohne Kacheln und ohne Kartendienst.
 *
 * Die Karte soll die Route zeigen, nicht navigieren: sie muss offline
 * funktionieren, in eine Druckseite passen und darf keine fremde Verbindung
 * aufmachen. Für echte Navigation liegen die Koordinaten im Datenmodell
 * bereit und lassen sich an MapLibre oder Leaflet weiterreichen.
 */

/** Bildpunkt in der Zeichenfläche. */
export type Point = [number, number]

/** Bildet eine Koordinate auf die Zeichenfläche ab. */
export type Projection = (lat: number, lon: number) => Point

/**
 * Mercator-Abbildung der geografischen Breite - in Grad, nicht in Bogenmass.
 *
 * Die Umrechnung am Ende ist nicht Kosmetik, sondern der Grund, aus dem diese
 * Karte überhaupt eine Karte ist. Die Formel liefert Bogenmass, die
 * Längengrade kommen in fitProjection aber in Grad an, und beide Achsen teilen
 * sich denselben Massstab. Ohne den Faktor stünde eine Breite gegen eine Länge
 * im Verhältnis 1 zu 57: die Route lag als nahezu waagerechter Strich über der
 * Fläche, und von einer Reise von Bern bis ans Schwarze Meer war die
 * Nord-Süd-Ausdehnung nicht mehr zu sehen. Dass ausgerechnet die
 * Balkanroute überwiegend in Ost-West-Richtung verläuft, hat das lange
 * verdeckt.
 */
export function mercatorY(lat: number): number {
  const clamped = Math.max(-85, Math.min(85, lat))
  return (Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360)) * 180) / Math.PI
}

/**
 * Baut eine Projektion, die alle übergebenen Punkte mit Rand in die Fläche
 * legt und dabei das Seitenverhältnis erhält.
 *
 * @param points Alles, was sichtbar sein muss: Route und Küstenlinien.
 * @param width Breite der Zeichenfläche in Bildpunkten.
 * @param height Höhe der Zeichenfläche in Bildpunkten.
 * @param padding Rand in Bildpunkten.
 */
export function fitProjection(
  points: LatLon[],
  width: number,
  height: number,
  padding = 26,
): Projection {
  if (points.length === 0) return () => [width / 2, height / 2]

  let minY = Infinity
  let maxY = -Infinity
  let minX = Infinity
  let maxX = -Infinity

  for (const [lat, lon] of points) {
    const y = mercatorY(lat)
    if (y < minY) minY = y
    if (y > maxY) maxY = y
    if (lon < minX) minX = lon
    if (lon > maxX) maxX = lon
  }

  const spanX = maxX - minX || 1
  const spanY = maxY - minY || 1
  const usableWidth = Math.max(1, width - padding * 2)
  const usableHeight = Math.max(1, height - padding * 2)

  const scale = Math.min(usableWidth / spanX, usableHeight / spanY)
  const offsetX = padding + (usableWidth - spanX * scale) / 2
  const offsetY = padding + (usableHeight - spanY * scale) / 2

  return (lat, lon) => [
    offsetX + (lon - minX) * scale,
    // In SVG wächst y nach unten, auf der Karte nach oben.
    offsetY + (maxY - mercatorY(lat)) * scale,
  ]
}

/** Formt eine Punktfolge in das points-Attribut eines SVG-Polygons um. */
export function toPolygonPoints(coords: LatLon[], project: Projection): string {
  return coords.map(([lat, lon]) => project(lat, lon).join(',')).join(' ')
}
